import { emitMobileTelemetryEvent } from '../../mobile/navigation/telemetry';
import { isMobileBrowser } from '../environment/utils';
import JitsiMeetJS, { JitsiTrackErrors, browser } from '../lib-jitsi-meet';
import { exactCameraDeviceId, isRoleDeviceId } from '../lib-jitsi-meet/exactCameraDeviceId';
import { gumPending, setAudioMuted } from '../media/actions';
import { MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import { getStartWithAudioMuted, isVideoMuted } from '../media/functions';
import { IGUMPendingState } from '../media/types';
import { toState } from '../redux/functions';
import { getUserSelectedCameraDeviceId, getUserSelectedMicDeviceId } from '../settings/functions.web';
import { shortFingerprint } from '../util/shortFingerprint';
import { getCameraFacingMode, getLocalJitsiVideoTrack, setTrackMuted } from './functions.any';
import loadEffects from './loadEffects';
import logger from './logger';
import { getMediaErrorName } from './mediaErrors';
import { notifyCameraSubstituted } from './cameraSubstitutionNotice.web';
import { acquireOtherCamera } from './otherCamera.web';
import { cameraLabelForId, findCameraIdByLabel } from './preferredCamera.web';
export * from './functions.any';
/**
 * Compares the camera a request named with the one the browser opened
 * (track.getSettings().deviceId) and reports a difference once. An exact
 * request should never differ; a difference means a browser that ignores
 * {exact}, or a deliberate fallback (createLocalTracksWithCameraFallback).
 *
 * @param {Array} tracks - The tracks just created.
 * @param {string} requestedId - The camera id that was asked for.
 * @param {string} fallback - How the camera was reached: 'none', 'label' or 'default'.
 * @returns {void}
 */
function reportCameraMismatch(tracks, requestedId, fallback) {
    try {
        for (const track of tracks) {
            if (track?.getType?.() !== MEDIA_TYPE.VIDEO || track.getVideoType?.() === 'desktop') {
                continue;
            }
            const mediaStreamTrack = track.getTrack?.();
            const obtainedId = mediaStreamTrack?.getSettings?.()?.deviceId;
            if (obtainedId && obtainedId !== requestedId) {
                emitMobileTelemetryEvent('camera_device_mismatch', {
                    fallback,
                    obtainedCameraRef: shortFingerprint(obtainedId),
                    obtainedLabel: String(mediaStreamTrack.label || '').slice(0, 60) || undefined,
                    requestedCameraRef: shortFingerprint(requestedId)
                });
            }
        }
    }
    catch (error) {
        // Diagnostics only: never fail a camera that did open.
        logger.warn('Camera mismatch check failed', error);
    }
}
/**
 * Create local tracks of specific types.
 *
 * @param {Object} options - The options with which the local tracks are to be
 * created.
 * @param {boolean} [options.browserDefaultCamera] - Name no camera at all, so
 * the browser opens its default. Unlike an undefined cameraDeviceId, the saved
 * camera is not filled in.
 * @param {string|null} [options.cameraDeviceId] - Camera device id or
 * {@code undefined} to use app's settings. Requested exactly (see
 * exactCameraDeviceId), so a stale id rejects instead of opening another camera.
 * @param {Object} [options.constraints] - Caller constraints. When given,
 * config.resolution is not forwarded: the lib would overwrite their height and
 * width with it.
 * @param {string[]} options.devices - Required track types such as 'audio'
 * and/or 'video'.
 * @param {string|null} [options.micDeviceId] - Microphone device id or
 * {@code undefined} to use app's settings.
 * @param {number|undefined} [oprions.timeout] - A timeout for JitsiMeetJS.createLocalTracks used to create the tracks.
 * @param {boolean} [options.firePermissionPromptIsShownEvent] - Whether lib-jitsi-meet
 * should check for a {@code getUserMedia} permission prompt and fire a
 * corresponding event.
 * @param {IStore} store - The redux store in the context of which the function
 * is to execute and from which state such as {@code config} is to be retrieved.
 * @returns {Promise<JitsiLocalTrack[]>}
 */
export function createLocalTracksF(options = {}, store) {
    let { cameraDeviceId, micDeviceId } = options;
    const { desktopSharingSourceDevice, desktopSharingSources, firePermissionPromptIsShownEvent, timeout } = options;
    // TODO The app's settings should go in the redux store and then the
    // reliance on the global variable APP will go away.
    store = store || APP.store; // eslint-disable-line no-param-reassign
    if (!store) {
        throw new Error('Track creation requires a Redux store.');
    }
    const state = store.getState();
    if (options.browserDefaultCamera) {
        cameraDeviceId = undefined;
    }
    else if (typeof cameraDeviceId === 'undefined' || cameraDeviceId === null) {
        cameraDeviceId = getUserSelectedCameraDeviceId(state);
    }
    if (typeof micDeviceId === 'undefined' || micDeviceId === null) {
        micDeviceId = getUserSelectedMicDeviceId(state);
    }
    const { desktopSharingFrameRate, firefox_fake_device, // eslint-disable-line camelcase
    resolution } = state['features/base/config'];
    const constraints = options.constraints ?? state['features/base/config'].constraints;
    const requestedCameraId = options.devices?.includes(MEDIA_TYPE.VIDEO) && cameraDeviceId
        && !isRoleDeviceId(cameraDeviceId) ? cameraDeviceId : undefined;
    return (loadEffects(store).then((effectsArray) => {
        // Filter any undefined values returned by Promise.resolve().
        const effects = effectsArray.filter(effect => Boolean(effect));
        return JitsiMeetJS.createLocalTracks({
            cameraDeviceId: exactCameraDeviceId(cameraDeviceId),
            constraints,
            desktopSharingFrameRate,
            desktopSharingSourceDevice,
            desktopSharingSources,
            // Copy array to avoid mutations inside library.
            devices: options.devices?.slice(0),
            effects,
            facingMode: options.facingMode || getCameraFacingMode(state),
            firefox_fake_device,
            firePermissionPromptIsShownEvent,
            micDeviceId,
            // The lib replaces height/width with this whenever it is
            // set, so a caller's own constraints would never reach the
            // browser (the old 360p camera retry never did).
            resolution: options.constraints ? undefined : resolution,
            timeout
        })
            .then((tracks) => {
            requestedCameraId && reportCameraMismatch(tracks, requestedCameraId, 'none');
            return tracks;
        })
            .catch((err) => {
            logger.error('Failed to create local tracks', options.devices, err);
            return Promise.reject(err);
        });
    }));
}
/**
 * Whether a getUserMedia failure means the named camera is not there: a
 * stale or missing id. Only a deviceId constraint counts; a resolution the
 * camera cannot meet is not a missing camera. NotFoundError does not say
 * which device is missing, so it counts only when no microphone was asked for.
 *
 * @param {unknown} error - The rejection from track creation.
 * @param {string[]} devices - The media types that were requested.
 * @returns {boolean}
 */
function isMissingCameraError(error, devices) {
    const name = getMediaErrorName(error);
    if (name === 'NotFoundError' || name === JitsiTrackErrors.NOT_FOUND) {
        return !devices.includes(MEDIA_TYPE.AUDIO);
    }
    if (name !== 'OverconstrainedError') {
        return false;
    }
    // lib-jitsi-meet keeps the browser's DOMException (and its constraint) at gum.error.
    const raw = error?.gum?.error
        ?? error;
    return !raw?.constraint || raw.constraint === 'deviceId';
}
/**
 * The browser's current cameras. The page's device list can predate the
 * change that made the id stale, or camera permission.
 *
 * @returns {Promise<MediaDeviceInfo[]>}
 */
async function enumerateCameras() {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter(device => device.kind === 'videoinput');
    }
    catch {
        return [];
    }
}
/**
 * {@link createLocalTracksF} that still gets the user video when the named
 * camera is gone. The camera is requested exactly, so a stale id (a camera
 * moved to another port, a saved id from elsewhere, an id passed through
 * before permission) now rejects with OverconstrainedError/NotFoundError where
 * an ideal id used to fall back silently. Then, at most two more requests:
 * the same camera found again by its label, and finally no id at all (the
 * browser default). Any other failure, and every failure of a request that
 * names no camera, is passed through untouched.
 *
 * @param {ITrackOptions} options - As for createLocalTracksF.
 * @param {IStore} store - The redux store.
 * @returns {Promise<JitsiLocalTrack[]>}
 */
export async function createLocalTracksWithCameraFallback(options = {}, store) {
    try {
        return await createLocalTracksF(options, store);
    }
    catch (error) {
        const state = (store || APP.store).getState();
        const devices = options.devices || [];
        const requestedId = devices.includes(MEDIA_TYPE.VIDEO) && !options.browserDefaultCamera
            ? options.cameraDeviceId ?? getUserSelectedCameraDeviceId(state)
            : undefined;
        if (!requestedId || isRoleDeviceId(requestedId) || !isMissingCameraError(error, devices)) {
            throw error;
        }
        const report = {
            failure_name: getMediaErrorName(error),
            requestedCameraRef: shortFingerprint(requestedId)
        };
        const errorNames = [];
        const attempt = async (fallback, cameraDeviceId) => {
            const tracks = await createLocalTracksF(fallback === 'default'
                ? { ...options,
                    browserDefaultCamera: true,
                    cameraDeviceId: undefined }
                : { ...options,
                    cameraDeviceId }, store);
            emitMobileTelemetryEvent('camera_device_fallback', {
                ...report,
                attempts: errorNames.length + 1,
                error_names: errorNames,
                outcome: fallback
            });
            reportCameraMismatch(tracks, requestedId, fallback);
            // The label fallback reopens the same physical camera under its
            // new id; the browser default is a different camera, so say so.
            if (fallback === 'default') {
                const videoTrack = tracks.find((track) => track?.getType?.() === MEDIA_TYPE.VIDEO);
                notifyCameraSubstituted((store || APP.store)?.dispatch, {
                    obtainedLabel: videoTrack?.getTrack?.()?.label || undefined,
                    reason: 'missing',
                    requestedLabel: cameraLabelForId(state, requestedId)
                });
            }
            return tracks;
        };
        const label = cameraLabelForId(state, requestedId);
        const relabelledId = label ? findCameraIdByLabel(await enumerateCameras(), label, requestedId) : undefined;
        if (relabelledId) {
            try {
                return await attempt('label', relabelledId);
            }
            catch (labelError) {
                const labelErrorName = getMediaErrorName(labelError);
                errorNames.push(labelErrorName);
                // A refusal covers every camera; asking again only re-prompts.
                if (labelErrorName === JitsiTrackErrors.PERMISSION_DENIED) {
                    emitMobileTelemetryEvent('camera_device_fallback', {
                        ...report,
                        attempts: 1,
                        error_names: errorNames,
                        outcome: 'failed'
                    });
                    throw labelError;
                }
            }
        }
        try {
            return await attempt('default');
        }
        catch (defaultError) {
            errorNames.push(getMediaErrorName(defaultError));
            emitMobileTelemetryEvent('camera_device_fallback', {
                ...report,
                attempts: errorNames.length,
                error_names: errorNames,
                outcome: 'failed'
            });
            // The last attempt's error describes the camera actually tried
            // (e.g. NotReadableError when the default camera is busy), which
            // is what the callers' retry and other-camera logic key on.
            throw defaultError;
        }
    }
}
/**
 * One camera for a camera-on after the join (turning the camera on, reopening
 * a stopped camera, a device-list change): {@link createLocalTracksWithCameraFallback},
 * and when the camera it reached will not start (busy in another app, or
 * gone), each other camera once, as the join does (acquireOtherCamera). A bare
 * id used to let desktop Chromium open the camera it ranked first, often a
 * free one; an exact id fails instead, so a saved camera held by Teams or
 * another browser left the call without video. An explicit camera choice does
 * not use this: that camera failing should show.
 *
 * @param {ITrackOptions} options - As for createLocalTracksF; video only.
 * @param {IStore} store - The redux store.
 * @returns {Promise<JitsiLocalTrack[]>}
 */
export async function createCameraTrackWithFallbacks(options = {}, store) {
    try {
        return await createLocalTracksWithCameraFallback(options, store);
    }
    catch (error) {
        const devices = options.devices || [];
        if (devices.length !== 1 || devices[0] !== MEDIA_TYPE.VIDEO || options.browserDefaultCamera) {
            throw error;
        }
        const getState = () => (store || APP.store).getState();
        return acquireOtherCamera({
            createVideoTrack: cameraDeviceId => createLocalTracksF({ ...options,
                cameraDeviceId }, store),
            emit: emitMobileTelemetryEvent,
            failure: error,
            getState,
            isCurrent: () => true,
            triedDeviceId: options.cameraDeviceId || getUserSelectedCameraDeviceId(getState()) || undefined
        });
    }
}
/**
 * One reopen per muted camera: the toolbar's camera-on dispatches
 * SET_VIDEO_MUTED, then conference.muteVideo dispatches it again while the
 * first request is still opening the camera.
 */
const cameraReopens = new WeakMap();
/**
 * Whether a local camera track was muted by stopping its stream, which is how
 * lib-jitsi-meet mutes a camera in every browser
 * (browser.doesVideoMuteByStreamRemove()).
 *
 * @param {JitsiLocalTrack} track - The track.
 * @returns {boolean}
 */
function isStreamRemovedCamera(track) {
    return Boolean(track?.isVideoTrack?.() && track.getVideoType?.() === VIDEO_TYPE.CAMERA
        && track.isMuted?.() && !track.getOriginalStream?.());
}
/**
 * Opens the camera a muted track held and hands it to the conference in the
 * muted track's place.
 *
 * @param {JitsiLocalTrack} track - The muted camera track.
 * @param {Function} dispatch - The redux dispatch.
 * @returns {Promise<void>}
 */
async function reopenMutedCamera(track, dispatch) {
    const isCurrent = () => getLocalJitsiVideoTrack(APP.store.getState()) === track;
    let camera;
    dispatch(gumPending([MEDIA_TYPE.VIDEO], IGUMPendingState.PENDING_UNMUTE));
    try {
        [camera] = await createCameraTrackWithFallbacks({
            cameraDeviceId: track.getDeviceId(),
            devices: [MEDIA_TYPE.VIDEO]
        });
        // Turned off again while the camera opened: publish it muted, as a
        // camera switch does, so the room keeps the user's latest choice.
        if (isVideoMuted(APP.store.getState())) {
            await camera.mute();
        }
        // conference.js owns the one local video slot and queues every
        // replacement; if this muted track was replaced meanwhile (a camera
        // switch, a device change) the new camera is disposed there instead.
        await APP.conference.useVideoStream(camera, undefined, isCurrent);
    }
    catch (error) {
        // Capture can succeed while publication fails. Release only this
        // unused camera, never the conference's current one.
        if (camera && getLocalJitsiVideoTrack(APP.store.getState()) !== camera) {
            try {
                await camera.dispose();
            }
            catch (disposeError) {
                if (disposeError?.name !== JitsiTrackErrors.TRACK_IS_DISPOSED) {
                    logger.warn('Unable to release the unused camera track.');
                }
            }
        }
        throw error;
    }
    finally {
        dispatch(gumPending([MEDIA_TYPE.VIDEO], IGUMPendingState.NONE));
    }
}
/**
 * The user's camera and microphone controls: {@link setTrackMuted}, except that
 * a camera muted by stopping its stream is reopened here rather than by
 * lib-jitsi-meet's unmute. The lib asks for track.getDeviceId() as a bare id,
 * which overwrites the {exact} kept in the track's constraints; desktop
 * Chromium does not use a bare (ideal) id to choose, so camera-on opened the
 * browser's default camera, or failed with NotReadableError when another app
 * held it. The lib keeps the old id on the track, so the picker then showed
 * the chosen camera as live and picking it did nothing. The camera is
 * requested exactly instead, with the stale-id and busy-camera fallbacks
 * (createCameraTrackWithFallbacks), and the new track replaces the muted one.
 *
 * @param {JitsiLocalTrack} track - The track to mute or unmute.
 * @param {boolean} muted - Whether to mute.
 * @param {Object} state - The redux state.
 * @param {Function} dispatch - The redux dispatch.
 * @returns {Promise}
 */
export function setLocalTrackMuted(track, muted, state, dispatch) {
    if (muted || !isStreamRemovedCamera(track)) {
        return setTrackMuted(track, muted, state, dispatch);
    }
    let reopen = cameraReopens.get(track);
    if (!reopen) {
        reopen = reopenMutedCamera(track, dispatch).finally(() => cameraReopens.delete(track));
        cameraReopens.set(track, reopen);
    }
    return reopen;
}
/**
 * Returns an object containing a promise which resolves with the created tracks and the errors resulting from that
 * process.
 *
 * @returns {Promise<JitsiLocalTrack[]>}
 *
 * @todo Refactor to not use APP.
 */
export function createPrejoinTracks(options = {}) {
    const errors = {};
    const initialDevices = [MEDIA_TYPE.AUDIO];
    const requestedAudio = true;
    let requestedVideo = false;
    const { startAudioOnly, startWithVideoMuted } = APP.store.getState()['features/base/settings'];
    const effectiveStartWithVideoMuted = options.forceStartWithVideoMuted || startWithVideoMuted;
    const startWithAudioMuted = getStartWithAudioMuted(APP.store.getState());
    // On Electron there is no permission prompt for granting permissions. That's why we don't need to
    // spend much time displaying the overlay screen. If GUM is not resolved within 15 seconds it will
    // probably never resolve.
    const timeout = browser.isElectron() ? 15000 : 60000;
    // Always get a handle on the audio input device so that we have statistics even if the user joins the
    // conference muted. Previous implementation would only acquire the handle when the user first unmuted,
    // which would results in statistics ( such as "No audio input" or "Are you trying to speak?") being available
    // only after that point.
    if (startWithAudioMuted) {
        APP.store.dispatch(setAudioMuted(true));
    }
    if (!effectiveStartWithVideoMuted && !startAudioOnly) {
        initialDevices.push(MEDIA_TYPE.VIDEO);
        requestedVideo = true;
    }
    let tryCreateLocalTracks = Promise.resolve([]);
    const { dispatch } = APP.store;
    dispatch(gumPending(initialDevices, IGUMPendingState.PENDING_UNMUTE));
    if (requestedAudio || requestedVideo) {
        tryCreateLocalTracks = createLocalTracksF({
            devices: initialDevices,
            firePermissionPromptIsShownEvent: true,
            timeout
        }, APP.store)
            .catch(async (err) => {
            if (err.name === JitsiTrackErrors.TIMEOUT && !browser.isElectron()) {
                errors.audioAndVideoError = err;
                return [];
            }
            // Retry with separate gUM calls.
            const gUMPromises = [];
            const tracks = [];
            if (requestedAudio) {
                gUMPromises.push(createLocalTracksF({
                    devices: [MEDIA_TYPE.AUDIO],
                    firePermissionPromptIsShownEvent: true,
                    timeout
                }));
            }
            if (requestedVideo) {
                gUMPromises.push(createLocalTracksF({
                    devices: [MEDIA_TYPE.VIDEO],
                    firePermissionPromptIsShownEvent: true,
                    timeout
                }));
            }
            const results = await Promise.allSettled(gUMPromises);
            let errorMsg;
            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') {
                    tracks.push(result.value[0]);
                }
                else {
                    errorMsg = result.reason;
                    const isAudio = idx === 0;
                    logger.error(`${isAudio ? 'Audio' : 'Video'} track creation failed with error ${errorMsg}`);
                    if (isAudio) {
                        errors.audioOnlyError = errorMsg;
                    }
                    else {
                        errors.videoOnlyError = errorMsg;
                    }
                }
            });
            if (errors.audioOnlyError && errors.videoOnlyError) {
                errors.audioAndVideoError = errorMsg;
            }
            return tracks;
        })
            .finally(() => {
            dispatch(gumPending(initialDevices, IGUMPendingState.NONE));
        });
    }
    return {
        tryCreateLocalTracks,
        errors
    };
}
/**
 * Determines whether toggle camera should be enabled or not.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} - Whether toggle camera should be enabled.
 */
export function isToggleCameraEnabled(stateful) {
    const state = toState(stateful);
    const { videoInput } = state['features/base/devices'].availableDevices;
    return isMobileBrowser() && Number(videoInput?.length) > 1;
}
